import { Button, Juggling, JugglingScreen } from "@motleyhand/design"
import { useNavigate } from "@tanstack/react-router"
import { useEffect } from "react"
import { toast } from "sonner"
import {
  useChangeSubscriptionMutation,
  useGetProductsQuery,
  useGetSubscriptionQuery,
} from "./api"
import Products from "./Products"
import { getBase, getCart, getIsDirty, init, useSelector } from "./slice"
import { ensure, formatPrice, invariant } from "@/utils"
import { useCurrentTenant } from "@/tenant"
import { ProductId } from "@/money/model"
import { useDispatch } from "@/base"

type Props = {
  base?: ProductId
}

export default function PurchasePage({ base }: Props) {
  const { tenantSlug } = useCurrentTenant()
  const dispatch = useDispatch()
  const { data: subscription, isLoading: isSubscriptionLoading } =
    useGetSubscriptionQuery()
  const cart = useSelector(getCart)
  const isDirty = useSelector(getIsDirty)
  const selectedBase = useSelector(getBase)
  const { data: products, isLoading: isProductsLoading } = useGetProductsQuery()
  const navigate = useNavigate()
  const [changeSubscription, { isLoading: isChangeSubscriptionLoading }] =
    useChangeSubscriptionMutation()

  const isCancelled = !!subscription?.cancelAt
  const isFreeSelected = selectedBase === ProductId.Twiddler
  const total = products
    ? cart.items.reduce((acc, { productId, quantity }) => {
        const product = ensure(
          products.find((product) => product.id === productId),
        )

        return (
          acc +
          (cart.interval === "month"
            ? product.monthlyPrice
            : product.yearlyPrice
          ).amount *
            quantity
        )
      }, 0) / 100
    : 0

  const handleCheckout = async () => {
    if (subscription) {
      try {
        await changeSubscription({ cart }).unwrap()
        toast.success("Your subscription was updated")
      } catch {
        toast.error("Failed to update subscription")
      }
    } else {
      await navigate({
        to: "/t/$tenantSlug/checkout",
        params: { tenantSlug },
        search: { cart },
      })
    }
  }

  useEffect(() => {
    if (isSubscriptionLoading || isProductsLoading) {
      return
    }

    invariant(products)
    const resolvedBase =
      base ??
      (isCancelled
        ? ProductId.Twiddler
        : (subscription?.baseProductId ?? ProductId.Twiddler))
    dispatch(
      init({
        base: resolvedBase,
        addons: isCancelled
          ? []
          : (subscription?.addonItems.filter(({ productId }) => {
              const addOnProduct = products.find(({ id }) => id === productId)

              return (
                addOnProduct?.productType === "addon" &&
                addOnProduct.forBases.includes(resolvedBase)
              )
            }) ?? []),
        interval: subscription?.interval ?? "month",
      }),
    )
  }, [
    isSubscriptionLoading,
    subscription,
    isCancelled,
    isProductsLoading,
    products,
    base,
    dispatch,
  ])

  if (isSubscriptionLoading) {
    return <JugglingScreen />
  }

  return (
    <div className="flex flex-row gap-4 p-4">
      <Products page="purchase" />
      <div className="sticky top-2 max-w-56 self-start">
        <div>
          <p className="text-lg font-bold text-nowrap">
            {formatPrice(total, 0)} / month
          </p>
          {subscription && (
            <p className="text-sm">
              Your billing will be automatically adjusted to reflect the change.
            </p>
          )}
        </div>
        <Button
          className="mt-2 w-full"
          onClick={handleCheckout}
          disabled={
            isChangeSubscriptionLoading || // Submitting or waiting for webhook
            (isCancelled && isFreeSelected) || // Already cancelled
            (!isCancelled && !!subscription && !isDirty) || // Same paid package
            (!subscription && isFreeSelected) // Same free package
          }
        >
          {isChangeSubscriptionLoading && <Juggling />}
          {isCancelled
            ? "Uncancel Subscription"
            : subscription
              ? "Change Subscription"
              : "Buy Subscription"}
        </Button>
      </div>
    </div>
  )
}
